'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useURLParams } from '@/lib/navigation/useURLParams'
import { Card } from '@/ui/cards/card/Card'
import { Title } from '@/ui/text/title/Title'
import { gql } from '@gql-generated/gql'
import type { ProductListActiveFiltersFragment } from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { isProductListPageLoadingAtom } from '../../services/productList.store'
import { ProductListActiveFilter } from './ProductListActiveFilter'

gql(/* GraphQL */ `
  fragment ProductListActiveFilters on ProductListFilter {
    title
    values {
      ...ProductListFilterValue
    }
  }
`)

type ProductListActiveFiltersProps = {
  productActiveFilters?: ProductListActiveFiltersFragment[] | null
  categoryActiveFilters?: ProductListActiveFiltersFragment[] | null
}

export function ProductListActiveFilters({
  productActiveFilters,
  categoryActiveFilters,
}: ProductListActiveFiltersProps) {
  const { setURLParams, removeURLParams } = useURLParams()
  const setIsProductListPageLoading = useSetAtom(isProductListPageLoadingAtom)
  const { t } = useTranslation(['productListActiveFilters'])

  const handleReset = () => {
    setURLParams([{ name: 'p', value: '1' }])
    removeURLParams([{ name: 'filter' }])
    setIsProductListPageLoading(true)
  }

  if (!productActiveFilters && !categoryActiveFilters) return null

  return (
    <Card className="hidden flex-col gap-1 px-3 py-2 desktop:flex" radius="sm">
      <Title as="h2" size="md" className="font-bold uppercase">
        {t('productListActiveFilters:currentFilters')}
      </Title>

      {categoryActiveFilters?.map((categoryActiveFilter) =>
        categoryActiveFilter.values?.map((value) => (
          <ProductListActiveFilter
            key={value.link.value + value.title}
            activeFilters={value as ProductListActiveFiltersFragment}
          />
        )),
      )}
      {productActiveFilters?.map((productActiveFilter) => (
        <ProductListActiveFilter
          key={productActiveFilter.title}
          activeFilters={productActiveFilter}
        />
      ))}

      {/* TODO: Update when Button V2 is ready */}
      <button
        data-testid="product-list-active-filters/clear-filters"
        className="text-left font-bold text-brand-2-900 underline"
        onClick={() => handleReset()}
      >
        {t('productListActiveFilters:clearFilters')}
      </button>
    </Card>
  )
}
